"use client";

import { useRef, type KeyboardEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import capImage from "@/app/assets/cap/CAP_PNG.png";
import { FormattedMessage, useIntl } from "react-intl";
import { FaBuilding, FaUserCircle } from "react-icons/fa";
import { FaArrowRight, FaCalendarDays, FaCheck } from "react-icons/fa6";
import { trackCtaClick, trackEvent } from "@/app/providers/ObservabilityProvider";
import { useAppLink } from "@/lib/analytics/app-links";
import { useLocale } from "@/app/providers/IntlProvider";
import { DEMO_URL } from "@/lib/demo";
import type { Profile } from "../../Utils/profileNav";
import { SPOT_TONES, type SpotTone } from "../Spot/SpotIcon";
import { LiveStage } from "../Story/LiveStage";
import { BAND_WIDTH, SECTION_GUTTER } from "../widths";
import { TeamGains } from "./TeamGains";

/** One argument of a profile: a scene and its copy (`story.<id>.title|desc`). */
type Argument = { id: string; tone: SpotTone };

/**
 * What each profile gets, from the sales argument, each list in story order. Individuals
 * get an organisation that follows their day, from morning to evening: the day ready each
 * morning, each task given its slot in the calendar, then noted on the phone and found on
 * the computer. Organisations get a tool that serves each person so the team moves: each
 * member keeps control of what they share (the adoption objection first), who does what
 * without follow-up meetings, then one document written together live.
 */
const ARGUMENTS: Record<Profile, Argument[]> = {
  Individual: [
    { id: "planday", tone: "amber" },
    { id: "agenda", tone: "sky" },
    { id: "devices", tone: "blue" },
  ],
  Organization: [
    { id: "privacy", tone: "green" },
    { id: "delegate", tone: "pink" },
    { id: "live", tone: "purple" },
  ],
};

/** The two profiles, labelled and iconed as the original home switch. */
const PROFILES: { id: Profile; key: "individual" | "organization"; Icon: typeof FaUserCircle }[] = [
  { id: "Individual", key: "individual", Icon: FaUserCircle },
  { id: "Organization", key: "organization", Icon: FaBuilding },
];

/**
 * « Who are you? »: the one fork of the home page. A two-tab switch (an individual, an
 * organisation) swaps three profile-specific arguments, each proven by its own live
 * animation, and a call to action fitted to the profile. The organisation tab adds what the
 * company gains (learnt in five minutes, a newcomer in one email, a tool people want to
 * open, finished work): a buyer's objections an individual does not have. Only the active
 * profile's cards are rendered, so navbar links to a card (`profile-<id>`) always land on a
 * visible one.
 *
 * @param profile - Active profile (owned by the home page: the navbar can switch it).
 * @param onProfileChange - Called when the visitor picks the other tab.
 */
export function ProfileSection({
  profile,
  onProfileChange,
}: {
  profile: Profile;
  onProfileChange: (p: Profile) => void;
}) {
  const intl = useIntl();
  const { locale } = useLocale();
  const current = PROFILES.find((p) => p.id === profile) ?? PROFILES[0];
  const placement = `profile-${current.key}`;
  const { href: appUrl, target: appTarget } = useAppLink(placement);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // Arrow keys move between the two tabs (WAI-ARIA tabs pattern). Up and down too: on
  // phones the two choices are stacked.
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>, k: number) => {
    const forward = e.key === "ArrowRight" || e.key === "ArrowDown";
    if (!forward && e.key !== "ArrowLeft" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const next = (k + (forward ? 1 : PROFILES.length - 1)) % PROFILES.length;
    onProfileChange(PROFILES[next].id);
    tabRefs.current[next]?.focus();
  };

  return (
    <section id="profils" aria-labelledby="profils-title" className={`scroll-mt-24 pb-16 pt-12 sm:pb-32 sm:pt-16 ${SECTION_GUTTER}`}>
      {/* The fork, as the original home had it: a brand-blue band with Cap peeking over its
          top edge, so the choice reads as a choice. Same width as the story panel above. */}
      <div className={`relative mx-auto pt-[5.25rem] sm:pt-20 ${BAND_WIDTH}`}>
        {/* Cap stands behind the band; the static wrapper keeps the hover area still while
            he rises, so he never jitters under a resting pointer. On phones he is centred
            over the stacked choice and shows his whole face, not just his beret. */}
        <div className="group absolute left-1/2 top-0 z-0 w-28 -translate-x-1/2 sm:left-[8%] sm:w-32 sm:translate-x-0">
          <Image
            src={capImage}
            alt=""
            aria-hidden="true"
            sizes="128px"
            className="h-auto w-full transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:-translate-y-8 motion-reduce:transition-none"
          />
        </div>

        <div className="relative z-10 flex flex-col items-center overflow-hidden rounded-[32px] bg-blue-main px-4 pb-6 pt-9 text-center min-[360px]:px-5 sm:rounded-[40px] sm:px-6 sm:py-16">
          <h2
            id="profils-title"
            className="text-balance text-[2rem] font-bold leading-[1.1] tracking-[-0.02em] text-white sm:text-5xl"
          >
            <FormattedMessage id="profile.title" />
          </h2>
          <p className="mt-4 max-w-2xl text-balance text-lg leading-relaxed text-white/85">
            <FormattedMessage id="profile.lead" />
          </p>

          {/* The switch: the chosen profile is a white pill. The white is the button's own
              background, not a sliding layer: the blue label can never end up on the blue band.
              On phones the two choices stack as full-width rows (thumb-sized, nothing cramped), each with a
              radio mark on the right; from sm they sit side by side in one pill. */}
          <div
            role="tablist"
            aria-label={intl.formatMessage({ id: "profile.tablist" })}
            // Outlined, not tinted: white labels keep 4.6:1 on the plain brand blue, a white
            // wash under them dropped the inactive tab to 3.6:1.
            className="mt-7 flex w-full flex-col gap-1.5 rounded-[26px] p-1.5 ring-1 ring-inset ring-white/45 sm:mt-10 sm:w-auto sm:flex-row sm:gap-0 sm:rounded-full"
          >
            {PROFILES.map(({ id, key, Icon }, k) => {
              const on = id === profile;
              return (
                <button
                  key={id}
                  ref={(el) => {
                    tabRefs.current[k] = el;
                  }}
                  id={`profile-tab-${key}`}
                  role="tab"
                  type="button"
                  aria-selected={on}
                  aria-controls="profile-panel"
                  tabIndex={on ? 0 : -1}
                  onClick={() => {
                    trackEvent("profile-tab", { profile: id });
                    onProfileChange(id);
                  }}
                  onKeyDown={(e) => onTabKey(e, k)}
                  className={`relative flex min-h-14 cursor-pointer items-center justify-start gap-3 rounded-[20px] px-4 py-3 text-base font-semibold transition-colors duration-300 sm:min-h-11 sm:min-w-48 sm:justify-center sm:rounded-full sm:px-7 sm:py-3 ${
                    on ? "bg-white text-blue-main shadow-md shadow-title-blue/20" : "text-white hover:bg-white/10"
                  } focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white`}
                >
                  <span className="relative flex items-center gap-3 whitespace-nowrap sm:gap-2.5">
                    {/* Same box for both icons on phones, so the stacked labels line up. */}
                    <Icon aria-hidden="true" className={`shrink-0 max-sm:w-6 ${key === "individual" ? "text-xl" : "text-base"}`} />
                    <FormattedMessage id={`profile.${key}.tab`} />
                  </span>
                  {/* Phones: a radio mark, so the stacked rows read as one choice of two. */}
                  <span
                    aria-hidden="true"
                    className={`relative ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-full sm:hidden ${
                      on ? "bg-blue-main text-white" : "ring-2 ring-inset ring-white/60"
                    }`}
                  >
                    {on && <FaCheck className="text-[0.7rem]" />}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* The cards hang from the band: same edges. */}
      <div className={`mx-auto ${BAND_WIDTH}`}>
        <div
          id="profile-panel"
          role="tabpanel"
          aria-labelledby={`profile-tab-${current.key}`}
          key={profile}
          className="story-in mt-12 sm:mt-20"
        >
          <p className="mx-auto max-w-3xl text-balance text-center text-2xl font-bold leading-snug tracking-[-0.015em] text-title-blue sm:text-3xl">
            <FormattedMessage id={`profile.${current.key}.lead`} />
          </p>

          {/* Picture beside text, one card per row, until three columns have room (lg). */}
          <ul className="mt-8 grid gap-6 sm:mt-14 lg:grid-cols-3 lg:gap-8 xl:gap-10">
            {ARGUMENTS[profile].map((a) => {
              const tone = SPOT_TONES[a.tone];
              const title = intl.formatMessage({ id: `story.${a.id}.title` });
              return (
                <li key={a.id} id={`profile-${a.id}`} className="scroll-mt-32">
                  <article className="flex h-full flex-col overflow-hidden rounded-[28px] bg-surface ring-1 ring-blue-light sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-center lg:flex">
                    <LiveStage
                      name={a.id}
                      locale={locale}
                      label={title}
                      replayLabel={intl.formatMessage({ id: "profile.replay" }, { title })}
                      className={`aspect-[5/4] ${tone.ground}`}
                    />
                    <div className="flex flex-1 flex-col px-6 pb-7 pt-5 sm:py-6 lg:pt-5">
                      <h3 className="text-balance text-xl font-bold leading-snug tracking-[-0.01em] text-title-blue xl:text-2xl">
                        {title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-text-main xl:text-base">
                        <FormattedMessage id={`story.${a.id}.desc`} />
                      </p>
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>

          {profile === "Organization" && <TeamGains />}

          <div className="mt-12 flex flex-col items-center justify-center gap-3 sm:mt-16 sm:flex-row">
            {profile === "Organization" && (
              <a
                href={DEMO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent("profile-demo-button", {}, { criticalOnNav: true });
                  trackCtaClick(placement, "demo", { profile: current.key });
                }}
                className="lift-hit flex w-full items-center justify-center gap-2 rounded-full bg-white px-7 py-3.5 text-base font-semibold text-blue-main ring-1 ring-blue-sec transition duration-300 ease-out hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-main/15 hover:ring-blue-main sm:w-auto"
              >
                <FormattedMessage id="profile.organization.demo" />
                <FaCalendarDays aria-hidden="true" />
              </a>
            )}
            <Link
              href={appUrl}
              onClick={() => {
                trackEvent("profile-signup-button", { profile }, { criticalOnNav: true });
                trackCtaClick(placement, appTarget, { profile: current.key });
              }}
              className="lift-hit group/cta flex w-full items-center justify-center gap-2 rounded-full bg-blue-main px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-blue-main/30 transition duration-300 ease-out hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-main/40 sm:w-auto"
            >
              <FormattedMessage id={`profile.${current.key}.cta`} />
              <FaArrowRight aria-hidden="true" className="text-sm transition-transform duration-200 group-hover/cta:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
